import React from "react";
import { FileText, Layers, Loader2, Printer, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { generateSimulationPdf, SIMULATION_REPORT_TYPES } from "@/lib/manufacturingSimulationPdf";

const REPORT_TYPES = [
  { id: SIMULATION_REPORT_TYPES.COMPACT, title: "Informe compacto", description: "Una hoja por artículo cuando sea posible: matriz y necesidades por proveedor y color, con merma.", icon: Printer },
  {
    id: SIMULATION_REPORT_TYPES.FULL,
    title: "Informe completo",
    description: "Detalle por artículo: cantidades por color y talla, materiales, procesos y costes.",
    icon: FileText,
  },
  {
    id: SIMULATION_REPORT_TYPES.NEEDS_BY_PROCESS,
    title: "Necesidades por procesos",
    description: "Materiales agrupados por procesos de fabricación con cantidad total e importe.",
    icon: Layers,
  },
  {
    id: SIMULATION_REPORT_TYPES.NEEDS_BY_SUPPLIER,
    title: "Necesidades por proveedor",
    description: "Materiales y procesos agrupados por proveedor, listo para enviar pedidos.",
    icon: Truck,
  },
];

export function SimulationPrintDialog({ open, onOpenChange, model }) {
  const [reportType, setReportType] = React.useState(SIMULATION_REPORT_TYPES.FULL);
  const [generating, setGenerating] = React.useState(false);
  const { toast } = useToast();

  const handleGenerate = async () => {
    if (!model?.items?.length) {
      toast({ title: "Nada que imprimir", description: "La simulación no contiene artículos.", variant: "destructive" });
      return;
    }
    setGenerating(true);
    try {
      const result = await generateSimulationPdf(model, { reportType });
      if (result?.error === "empty") {
        toast({ title: "Nada que imprimir", description: "La simulación no contiene artículos.", variant: "destructive" });
        return;
      }
      onOpenChange?.(false);
    } catch (error) {
      console.error("Error generando PDF de simulación:", error);
      toast({ title: "Error al generar el PDF", description: "No se pudo generar el documento.", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (generating ? null : onOpenChange?.(value))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Imprimir simulación</DialogTitle>
          <DialogDescription>Elige el tipo de informe que quieres generar en PDF.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {REPORT_TYPES.map((type) => {
            const Icon = type.icon;
            const active = reportType === type.id;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => setReportType(type.id)}
                className={`flex w-full items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors ${active ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"}`}
              >
                <span className={`mt-0.5 rounded-md p-2 ${active ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600"}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium">{type.title}</span>
                  <span className="block text-sm text-gray-600">{type.description}</span>
                </span>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={generating}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleGenerate} disabled={generating}>
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
            {generating ? "Generando..." : "Generar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
